// Stub responder — deterministic "make it harder" proposer.
//
// Walks a curated escalation ladder of mutations. The ladder interleaves
// two moves:
//   - RATCHET the length up (propose a LENGTH rule with a bigger n; the
//     lifecycle replaces the length target in place), and
//   - ADD a fresh constraint (character variety, structure, exclusions).
//
// Deliberately mechanical. Real intelligence is what ship #3's BabyAI
// responder brings; this stub exists so the loop demos end-to-end without
// a network round-trip, and so the lifecycle gates can be verified before
// the AI variability lands on top.
//
// Indexed by how many proposals have already run (one per cord pull), so a
// ratchet — which replaces a rule rather than adding one — still advances
// the ladder. When the ladder is exhausted, the fallback keeps escalating
// forever: ratchet toward the cap, then pile on fresh exclusions.

import { currentLengthTarget, MAX_LENGTH, type Predicate, type Rule } from "./dsl";
import { type State } from "./andon-loop";

// ─── Mutation constructors ──────────────────────────────────────────────────

/** A length ratchet that always lands strictly above the current target
 *  (and never past the cap), so the lifecycle's tier check never bounces it. */
function ratchet(state: Readonly<State>, toAtLeast: number): Rule {
  const cur = currentLengthTarget(state.rules);
  const n = Math.min(MAX_LENGTH, Math.max(toAtLeast, cur + 1));
  return { id: `r_len_${n}`, name: `length_${n}`, predicate: { kind: "LENGTH", op: "=", n } };
}

function add(name: string, predicate: Predicate): Rule {
  return { id: `r_${name}`, name, predicate };
}

// ─── The ladder ─────────────────────────────────────────────────────────────
//
// Each rung is a function of current state so ratchets stay relative. The
// starter ruleset is "3 digits"; the ladder grows length and weirdness from
// there, widening the alphabet (upper, symbol) before leaning on it.

const LADDER: Array<(s: Readonly<State>) => Rule> = [
  // Character variety first — numeric → alphanumeric → mixed case → symbols.
  // Length grows just ahead of each new required class so the ruleset stays
  // satisfiable (position 0 is a forced digit, so N required classes need
  // roughly N characters of room).
  ()  => add("allow_letters", { kind: "CONTAINS", class: "lower" }),     // 3-digit → 3-char alphanumeric
  ()  => add("no_repeat",     { kind: "NO_REPEAT" }),                    // no repeating chars
  ()  => add("needs_upper",   { kind: "CONTAINS", class: "upper" }),     // mixed case (digit+lower+upper fit in 3)
  (s) => ratchet(s, 4),                                                  // 3 → 4: make room for a symbol
  ()  => add("needs_symbol",  { kind: "CONTAINS", class: "symbol" }),    // demand a symbol
  ()  => add("no_triples",    { kind: "NO_RUN_OF_LENGTH", n: 3 }),
  (s) => ratchet(s, 6),                                                  // → 6: enough digits for a sum rule
  ()  => add("digit_sum_10",  { kind: "SUM_OF_DIGITS", op: ">=", n: 10 }),
  ()  => add("no_123",        { kind: "NOT_CONTAINS", pattern: "123" }),
  ()  => add("upper_at_1",    { kind: "POSITION", i: 1, class: "upper" }),
  (s) => ratchet(s, 8),                                                  // → 8
  ()  => add("no_double_zero", { kind: "NOT_CONTAINS", pattern: "00" }),
];

// ─── Fallback (when the ladder runs out) ────────────────────────────────────
//
// Keep the demo alive indefinitely: ratchet toward the cap two at a time,
// then once length is maxed, add fresh exclusions the ruleset doesn't have.

function fallbackProposal(state: Readonly<State>): Rule {
  const cur = currentLengthTarget(state.rules);
  if (cur < MAX_LENGTH) return ratchet(state, cur + 2);

  const existing = new Set(
    state.rules
      .filter((r) => r.predicate.kind === "NOT_CONTAINS")
      .map((r) => (r.predicate as Extract<Predicate, { kind: "NOT_CONTAINS" }>).pattern),
  );
  for (const pat of ["11", "22", "33", "99", "000", "111", "abc", "xyz"]) {
    if (!existing.has(pat)) return add(`no_${pat}`, { kind: "NOT_CONTAINS", pattern: pat });
  }
  // Last resort — harmless redundant structure rule.
  return add("no_quad_run", { kind: "NO_RUN_OF_LENGTH", n: 4 });
}

// ─── Responder ──────────────────────────────────────────────────────────────

export async function stubResponder(state: Readonly<State>): Promise<Rule> {
  // One proposal per cord pull; index by proposals already recorded so a
  // ratchet (which doesn't grow rules.length) still advances the ladder.
  const step = state.proposals.length;
  return step < LADDER.length ? LADDER[step](state) : fallbackProposal(state);
}
